import React, { useState } from "react";
import {
  ChildFormProps,
  EmployeeRelations,
  EmploymentHistory,
  PrevApplications,
} from "../../../types/Form-types";
import TextareaInput from "../common-components/TextAreaInput";
import { employment_textAreaQuestions } from "../form-data/interviewForm-data";
import InputCell from "./InputCell";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";
import { InterviewAssessmentTypes } from "../InterviewAssessmentForm";
import RadioCheckboxInput from "../common-components/RadioCheckboxInput";
import ErrorMessage from "../common-components/ErrorMessage";

// ------ employment data --------
const emptyRow_Employment: EmploymentHistory = {
  organization: "",
  duration_from: "",
  duration_to: "",
  last_designation: "",
  last_drawn_ctc: "",
  reason_for_change: "",
};

type EmploymentRow = typeof emptyRow_Employment;
const COLUMNS: {
  label: string;
  field: keyof EmploymentRow;
  type: "text" | "date" | "number";
}[] = [
  { label: "Organization", field: "organization", type: "text" },
  { label: "From", field: "duration_from", type: "date" },
  { label: "To", field: "duration_to", type: "date" },
  { label: "Last Designation", field: "last_designation", type: "text" },
  { label: "Last Drawn CTC", field: "last_drawn_ctc", type: "number" },
  { label: "Reason for Change", field: "reason_for_change", type: "text" },
];

// ------ employee relation data --------
const emptyRow_Relation: EmployeeRelations = {
  name: "",
  designation: "",
  relationship: "",
};
type EmployeeRelationRow = typeof emptyRow_Relation;
const COLUMNS_Relation: {
  label: string;
  field: keyof EmployeeRelationRow;
  type: "text" | "date";
}[] = [
  { label: "Name", field: "name", type: "text" },
  { label: "Designation", field: "designation", type: "text" },
  { label: "Relationship", field: "relationship", type: "text" },
];

// ------ Prev application data --------
const emptyRow_PrevApp: PrevApplications = {
  year_month_applied: "",
  position: "",
  location: "",
};
type PrevApplicationRow = typeof emptyRow_PrevApp;
const COLUMNS_PrevApp: {
  label: string;
  field: keyof PrevApplicationRow;
  type: "text" | "date";
}[] = [
  { label: "", field: "year_month_applied", type: "date" },
  { label: "", field: "position", type: "text" },
  { label: "", field: "location", type: "text" },
];

interface EmploymentComponentProps extends ChildFormProps {
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleFieldChange: (name: string, value: string) => void;
  handleError: (name: string) => void;
}

export const EmploymentHistorySection = ({
  formData,
  setFormData,
  errors,
  handleChange,
  handleFieldChange,
  handleError,
}: EmploymentComponentProps) => {
  // ✅ 1 row by default for employment
  const [rows, setRows] = useState<EmploymentHistory[]>([
    { ...emptyRow_Employment },
  ]);
  const [rowsRelation, setRowsRelation] = useState<EmployeeRelations[]>(
    Array(2)
      .fill(null)
      .map(() => ({ ...emptyRow_Relation })),
  );
  const [rowsPrevApp, setRowsPrevApp] = useState<PrevApplications[]>([
    { ...emptyRow_PrevApp },
  ]);

  const getValue = (e: any): string => {
    if (typeof e === "string") return e;
    return e?.target?.value ?? "";
  };

  // ✅ generic row handler with handleError support
  const makeRowChangeHandler =
    <T extends object>(
      setRowsFn: React.Dispatch<React.SetStateAction<T[]>>,
      formDataKey: string,
    ) =>
    (index: number, field: keyof T, value: string) => {
      setRowsFn((prevRows) => {
        const updated = prevRows.map((row, i) =>
          i === index ? { ...row, [field]: value } : row,
        );
        const mapped = updated
          .filter((row) => Object.values(row).some((v) => v !== ""))
          .map((row) => ({ ...row }));
        setFormData((prev: any) => ({ ...prev, [formDataKey]: mapped }));
        return updated;
      });
      // ✅ clear specific cell error
      handleError(`${formDataKey}.${index}.${String(field)}`);
    };

  const handleEmploymentChange = makeRowChangeHandler<EmploymentHistory>(
    setRows,
    "employment_history",
  );
  const handleRelationChange = makeRowChangeHandler<EmployeeRelations>(
    setRowsRelation,
    "employee_relations",
  );
  const handlePrevAppChange = makeRowChangeHandler<PrevApplications>(
    setRowsPrevApp,
    "prev_applications",
  );

  const handleDateChange = (
    index: number,
    field: keyof EmploymentHistory | keyof PrevApplications,
    dateString: string,
    format?: "MMYY",
    type?: "employment" | "prevApp",
  ) => {
    const formatted = dateString
      ? format == "MMYY"
        ? dayjs(dateString, "MM/YY").format("MM/YY")
        : dayjs(dateString, "DD-MM-YYYY").format("YYYY-MM-DD")
      : "";
    // handleEmploymentChange(index, field as any, formatted);
    if (type === "prevApp") {
      handlePrevAppChange(index, field as keyof PrevApplications, formatted); // ✅
    } else {
      handleEmploymentChange(
        index,
        field as keyof EmploymentHistory,
        formatted,
      );
    }
  };

  // ✅ add employment row
  const handleAddRow = () => {
    setRows((prev) => [...prev, { ...emptyRow_Employment }]);
  };

  // ✅ remove employment row
  const handleRemoveRow = (index: number) => {
    setRows((prevRows) => {
      const updated = prevRows.filter((_, i) => i !== index);
      const mapped = updated
        .filter((row) => Object.values(row).some((v) => v !== ""))
        .map((row) => ({ ...row }));
      setFormData((prev: any) => ({ ...prev, employment_history: mapped }));
      return updated;
    });
  };

  return (
    <div className="pt-3">
      {/* ── Header with Add Row */}
      <div className="mb-3 d-sm-flex align-items-center justify-content-between">
        <div>
          <p className="form-section-title mb-1">Employment History</p>
          <p
            className="text-muted fst-italic m-0"
            style={{ fontSize: "0.8rem" }}
          >
            (Current Employment onwards – In reverse chronological order, i.e.
            present employer first)
          </p>
        </div>
        <div className="text-end">
          <button
            type="button"
            onClick={handleAddRow}
            style={{
              background: "none",
              border: "1px dashed #fd7e14",
              color: "#fd7e14",
              borderRadius: "4px",
              padding: "4px 12px",
              fontSize: "0.82rem",
              cursor: "pointer",
            }}
          >
            + Add Row
          </button>
        </div>
      </div>

      {/* ── Employment Table */}
      <div className="table-responsive mb-4">
        <table className="form-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ minWidth: "160px" }}>
                Organization
              </th>
              <th colSpan={2}>Duration (mm/yy)</th>
              <th rowSpan={2} style={{ minWidth: "120px" }}>
                Last Designation
              </th>
              <th rowSpan={2} style={{ minWidth: "120px" }}>
                Last Drawn Salary (CTC)
              </th>
              <th rowSpan={2} style={{ minWidth: "120px" }}>
                Reason For Change
              </th>
              {rows.length > 1 && <th rowSpan={2}></th>}
            </tr>
            <tr>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, i) => (
              <tr key={i}>
                {COLUMNS?.map((col) => {
                  const errorMsg =
                    errors?.employment_history?.[i]?.[
                      col.field as keyof EmploymentHistory
                    ];
                  return (
                    <React.Fragment key={col.field}>
                      {col.type == "date" ? (
                        <td
                          style={{
                            padding: 0,
                            minWidth: "150px",
                            maxWidth: "150px",
                            width: "150px",
                          }}
                          className="px-2 py-1"
                        >
                          <DatePicker
                            name={col.field}
                            style={{
                              maxWidth: "100px",
                              marginTop: "-0.5rem",
                            }}
                            className="p-0 ps-1 form-control datetimepicker d-inline"
                            format={{ format: "MM/YY", type: "mask" }}
                            placeholder="MM/YY"
                            value={
                              row[col.field]
                                ? dayjs(row[col.field] as string, "MM/YY")
                                : null
                            }
                            disabledDate={(current) => {
                              // if (!row["duration_from"]) return false;
                              if (col.field == "duration_to") {
                                const start = dayjs(row.duration_from, "MM/YY");
                                return (
                                  current && current.isBefore(start, "month")
                                );
                              } else {
                                return (
                                  current && current.isAfter(dayjs(), "month")
                                );
                              }
                            }}
                            disabled={
                              col.field == "duration_to" &&
                              !row["duration_from"]
                            }
                            onChange={(
                              date: any,
                              dateString: string | string[],
                            ) => {
                              const formatted = date
                                ? typeof dateString === "string"
                                  ? dateString
                                  : dateString[0]
                                : "";
                              handleDateChange(i, col.field, formatted, "MMYY");
                            }}
                          />
                          <ErrorMessage message={errorMsg} />
                        </td>
                      ) : (
                        <InputCell
                          key={col.field}
                          type={col.type}
                          name={col.field}
                          value={String(row[col.field] ?? "")}
                          handleChange={(e) =>
                            handleEmploymentChange(i, col.field, getValue(e))
                          }
                          errorMessage={errorMsg}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {/* ✅ remove button */}
                {rows.length > 1 && (
                  <td style={{ textAlign: "center", width: 40 }}>
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(i)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "1rem",
                        padding: 0,
                      }}
                      title="Remove row"
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Open Questions */}
      <div className="mb-3">
        {employment_textAreaQuestions?.map((ques) => (
          <TextareaInput
            key={ques.name}
            label={ques?.label}
            name={ques?.name}
            rows={ques?.rows}
            value={String(
              formData[ques.name as keyof InterviewAssessmentTypes] ?? "",
            )}
            onChange={handleChange}
            errorMessage={errors?.[ques.name]}
          />
        ))}
      </div>

      {/* ── Strength & Weakness */}
      <div className="mb-3">
        <div className="pb-2">
          2.&nbsp;
          <span className="fw-semibold form-dark-text">
            List out any one strength and one weakness?
          </span>
        </div>
        <div className="ps-2">
          <TextareaInput
            label={"Write about your Strength"}
            name={"strength"}
            rows={2}
            value={String(formData["strength"] ?? "")}
            onChange={handleChange}
            errorMessage={errors?.["strength"]}
          />
          <TextareaInput
            label={"Write about your Weakness"}
            name={"weakness"}
            rows={2}
            value={String(formData["weakness"] ?? "")}
            onChange={handleChange}
            errorMessage={errors?.["weakness"]}
          />
        </div>
      </div>

      {/* ── Employee Relationship */}
      <div className="mb-3">
        <p className="mb-2" style={{ fontSize: "0.85rem", color: "#111827" }}>
          3.&nbsp;
          <span className="fw-semibold form-dark-text">
            Employee Relationship
          </span>
          <p className="mt-2">
            Do you have any relationship with any employee currently employed
            with the Team1 Consulting Pvt Ltd? Please mention details below.
          </p>
        </p>
        <div className="table-responsive">
          <table className="form-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Designation</th>
                <th>Relationship</th>
              </tr>
            </thead>
            <tbody>
              {rowsRelation?.map((row, i) => (
                <tr key={i}>
                  {COLUMNS_Relation?.map((col) => {
                    const errorMsg =
                      errors?.employee_relations?.[i]?.[
                        col.field as keyof EmployeeRelations
                      ];
                    return (
                      <InputCell
                        key={col.field}
                        name={col.field}
                        value={String(row[col.field] ?? "")}
                        handleChange={(e) =>
                          handleRelationChange(i, col.field, getValue(e))
                        }
                        errorMessage={errorMsg}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Previous Application */}
      <div className="mb-3">
        <p className="mb-2" style={{ fontSize: "0.85rem", color: "#111827" }}>
          4.&nbsp;
          <span className="fw-semibold form-dark-text">
            Previous Application
          </span>
          <p className="mt-2">
            Have you applied for any position in Team1 Consulting Pvt Ltd
            previously? If Yes:
          </p>
        </p>
        <div className="table-responsive">
          <table className="form-table">
            <thead>
              <tr>
                <th>Year/Month Applied</th>
                <th>Position</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {rowsPrevApp?.map((row, i) => (
                <tr key={i}>
                  {COLUMNS_PrevApp?.map((col) => {
                    const errorMsg =
                      errors?.prev_applications?.[i]?.[
                        col.field as keyof PrevApplications
                      ];
                    return (
                      <>
                        {col.type == "date" ? (
                          <td
                            style={{
                              padding: 0,
                              minWidth: "150px",
                              maxWidth: "150px",
                              width: "150px",
                            }}
                            className="px-2 py-1"
                          >
                            <DatePicker
                              name={col.field}
                              style={{
                                maxWidth: "80px",
                                marginTop: "-0.5rem",
                              }}
                              className="p-0 ps-1 form-control datetimepicker d-inline"
                              format={{ format: "MM/YY", type: "mask" }}
                              placeholder="MM/YY"
                              value={
                                row[col.field]
                                  ? dayjs(row[col.field], "MM/YY")
                                  : null
                              }
                              disabledDate={(current) =>
                                current && current.isAfter(dayjs(), "date")
                              }
                              onChange={(
                                date: any,
                                dateString: string | string[],
                              ) => {
                                const value = date
                                  ? typeof dateString === "string"
                                    ? dateString
                                    : dateString[0]
                                  : "";
                                handleDateChange(
                                  i,
                                  col.field,
                                  value,
                                  "MMYY",
                                  "prevApp",
                                );
                              }}
                            />
                          </td>
                        ) : (
                          <InputCell
                            key={col.field}
                            name={col.field}
                            value={String(row[col.field] ?? "")}
                            handleChange={(e) =>
                              handlePrevAppChange(i, col.field, getValue(e))
                            }
                            errorMessage={errorMsg}
                          />
                        )}
                      </>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── CTC + Conviction */}
      <div className="table-responsive mb-4">
        <table className="form-table">
          <tbody>
            <tr>
              <td className="td-label">
                Current CTC: <span className="text-danger">*</span>
              </td>
              <InputCell
                name={"current_ctc"}
                type="number"
                value={String(formData["current_ctc"] ?? "")}
                handleChange={(value) =>
                  handleFieldChange("current_ctc", value)
                }
                errorMessage={errors?.["current_ctc"]}
              />
              <td className="td-label">
                Expected CTC: <span className="text-danger">*</span>
              </td>
              <InputCell
                name={"expected_ctc"}
                type="number"
                value={String(formData["expected_ctc"] ?? "")}
                handleChange={(value) =>
                  handleFieldChange("expected_ctc", value)
                }
                errorMessage={errors?.["expected_ctc"]}
              />
            </tr>
            <tr>
              <td className="td-label" colSpan={2}>
                Notice Period required to be given to the present employer if
                selected (In Days) <span className="text-danger">*</span>
              </td>
              <InputCell
                colSpan={3}
                type="number"
                name="notice_period_days"
                value={String(formData["notice_period_days"] ?? "")}
                handleChange={(value) =>
                  handleFieldChange("notice_period_days", value)
                }
                errorMessage={errors?.["notice_period_days"]}
              />
            </tr>
            <tr>
              <td className="td-label" colSpan={2}>
                Have you ever been convicted in a court of law &nbsp;
                <span className="text-danger">*</span>
              </td>
              <td colSpan={2}>
                <div className="d-flex gap-4">
                  {["Yes", "No"].map((opt) => (
                    <RadioCheckboxInput
                      key={opt}
                      id={opt}
                      name="convicted_in_court"
                      value={opt}
                      label={opt}
                      checked={formData?.convicted_in_court === opt}
                      onChange={(e) =>
                        handleFieldChange("convicted_in_court", e.target.value)
                      }
                    />
                  ))}
                </div>
                <ErrorMessage message={errors?.["convicted_in_court"]} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
// import React, { useState } from "react";
// import {
//   ChildFormProps,
//   EmployeeRelations,
//   EmploymentHistory,
//   PrevApplications,
// } from "../../../types/Form-types";
// import TextareaInput from "../common-components/TextAreaInput";
// import { employment_textAreaQuestions } from "../form-data/interviewForm-data";
// import InputCell from "./InputCell";
// import dayjs from "dayjs";
// import { DatePicker } from "antd";
// import { InterviewAssessmentTypes } from "../InterviewAssessmentForm";
// import RadioCheckboxInput from "../common-components/RadioCheckboxInput";
// import ErrorMessage from "../common-components/ErrorMessage";

// // interface EmploymentProps {
// //   form: { employmentHistory: EmploymentHistory };
// // }

// // ------ employment data --------
// const emptyRow_Employment: EmploymentHistory = {
//   organization: "",
//   duration_from: "",
//   duration_to: "",
//   last_designation: "",
//   last_drawn_ctc: "",
//   reason_for_change: "",
// };

// type EmploymentRow = typeof emptyRow_Employment;
// const COLUMNS: {
//   label: string;
//   field: keyof EmploymentRow;
//   type: "text" | "date";
// }[] = [
//   { label: "Organization", field: "organization", type: "text" },
//   { label: "From", field: "duration_from", type: "date" },
//   { label: "To", field: "duration_to", type: "date" },
//   { label: "Last Designation", field: "last_designation", type: "text" },
//   { label: "Last Drawn CTC", field: "last_drawn_ctc", type: "text" },
//   { label: "Reason for Change", field: "reason_for_change", type: "text" },
// ];
// // ------ end employment data --------

// // ------ employee relation data --------
// const emptyRow_Relation: EmployeeRelations = {
//   name: "",
//   designation: "",
//   relationship: "",
// };
// type EmployeeRelationRow = typeof emptyRow_Relation;
// const COLUMNS_Relation: {
//   label: string;
//   field: keyof EmployeeRelationRow;
//   type: "text" | "date";
// }[] = [
//   { label: "Name", field: "name", type: "text" },
//   { label: "Designation", field: "designation", type: "text" },
//   { label: "Relationship", field: "relationship", type: "text" },
// ];
// // ------ end employee relation data --------

// // ------ Prev application data --------
// const emptyRow_PrevApp: PrevApplications = {
//   year_month_applied: "",
//   position: "",
//   location: "",
// };
// type PrevApplicationRow = typeof emptyRow_PrevApp;
// const COLUMNS_PrevApp: {
//   label: string;
//   field: keyof PrevApplicationRow;
//   type: "text" | "date";
// }[] = [
//   { label: "", field: "year_month_applied", type: "text" },
//   { label: "", field: "position", type: "text" },
//   { label: "", field: "location", type: "text" },
// ];
// // ------ end employee relation data --------

// interface EmploymentComponentProps extends ChildFormProps {
//   handleChange: (e: React.ChangeEvent<any>) => void;
//   handleFieldChange: (name: string, value: string) => void;
//   handleError: (name: string) => void;
// }

// export const EmploymentHistorySection = ({
//   formData,
//   setFormData,
//   errors,
//   handleChange,
//   handleFieldChange,
//   handleError,
// }: EmploymentComponentProps) => {
//   const [rows, setRows] = useState<EmploymentHistory[]>(
//     Array(5)
//       .fill(null)
//       .map(() => ({ ...emptyRow_Employment })),
//   );
//   const [rowsRelation, setRowsRelation] = useState<EmployeeRelations[]>(
//     Array(2)
//       .fill(null)
//       .map(() => ({ ...emptyRow_Relation })),
//   );
//   const [rowsPrevApp, setRowsPrevApp] = useState<PrevApplications[]>([
//     {
//       ...emptyRow_PrevApp,
//     },
//   ]);

//   const getValue = (e: any): string => {
//     if (typeof e === "string") return e;
//     return e?.target?.value ?? "";
//   };

//   // ---- generric row update handlere 0-----
//   const makeRowChangeHandler =
//     <T extends object>(
//       setRowsFn: React.Dispatch<React.SetStateAction<T[]>>,
//       formDataKey: string,
//     ) =>
//     (index: number, field: keyof T, value: string) => {
//       setRowsFn((prevRows) => {
//         const updated = prevRows.map((row, i) =>
//           i === index ? { ...row, [field]: value } : row,
//         );
//         const mapped = updated
//           .filter((row) => Object.values(row).some((v) => v !== ""))
//           .map((row) => ({ ...row }));
//         setFormData((prev: any) => ({ ...prev, [formDataKey]: mapped }));
//         return updated;
//       });
//     };

//   const createHandler = makeRowChangeHandler;

//   const handleEmploymentChange = createHandler<EmploymentHistory>(
//     setRows,
//     "employment_history",
//   );
//   const handleRelationChange = createHandler<EmployeeRelations>(
//     setRowsRelation,
//     "employee_relations",
//   );
//   const handlePrevAppChange = createHandler<PrevApplications>(
//     setRowsPrevApp,
//     "prev_applications",
//   );

//   const handleDateChange = (
//     index: number,
//     field: keyof EmploymentHistory,
//     dateString: string,
//     format?: "MMYY",
//   ) => {
//     const formatted = dateString
//       ? format == "MMYY"
//         ? dayjs(dateString, "MM/YY").format("MM/YY")
//         : dayjs(dateString, "DD-MM-YYYY").format("YYYY-MM-DD")
//       : "";
//     handleEmploymentChange(index, field, formatted);
//   };

//   // console.log("formdata:", formData);

//   return (
//     <div className="pt-3">
//       <p className="form-section-title mb-1">Employment History</p>
//       <p
//         className="text-muted fst-italic mb-4 2"
//         style={{ fontSize: "0.8rem" }}
//       >
//         (Current Employment onwards – In reverse chronological order, i.e.
//         present employer first)
//       </p>
//       <div className="table-responsive mb-4">
//         <table className="form-table">
//           <thead>
//             <tr>
//               <th rowSpan={2}>Organization</th>
//               <th colSpan={2}>Duration (mm/yy)</th>
//               <th rowSpan={2} style={{ minWidth: "120px" }}>
//                 Last Designation
//               </th>
//               <th rowSpan={2} style={{ minWidth: "120px" }}>
//                 Last Drawn Salary (CTC)
//               </th>
//               <th rowSpan={2} style={{ minWidth: "120px" }}>
//                 Reason For Change
//               </th>
//             </tr>
//             <tr>
//               <th>From</th>
//               <th>To</th>
//             </tr>
//           </thead>
//           <tbody>
//             {rows?.map((row, i) => (
//               <tr key={i}>
//                 {COLUMNS?.map((col) => {
//                   const errorMsg =
//                     errors?.employment_history?.[i]?.[
//                       col.field as keyof EmploymentHistory
//                     ];
//                   // console.log(
//                   //   col.field,
//                   //   errors?.employment_history?.[i]?.[col?.field],
//                   // );
//                   return (
//                     <React.Fragment key={col.field}>
//                       {col.type == "date" ? (
//                         <td
//                           key={col.field}
//                           style={{
//                             padding: 0,
//                             minWidth: "150px",
//                             maxWidth: "150px",
//                             width: "150px",
//                           }}
//                           className="px-2 py-1"
//                         >
//                           <DatePicker
//                             name={col.field}
//                             style={{
//                               // width: "100%",
//                               maxWidth: "100px",
//                               marginTop: "-0.5rem",
//                             }}
//                             className="p-0 ps-1 form-control datetimepicker  d-inline"
//                             format={{
//                               format: "MM/YY",
//                               type: "mask",
//                             }}
//                             placeholder="MM/YY"
//                             value={
//                               row[col.field]
//                                 ? dayjs(row[col.field] as string, "MM/YY")
//                                 : ""
//                             }
//                             onChange={(
//                               date: any,
//                               dateString: string | string[],
//                             ) => {
//                               // if (!date) return;
//                               const formatted = date
//                                 ? typeof dateString === "string"
//                                   ? dateString
//                                   : dateString[0]
//                                 : "";
//                               handleDateChange(i, col.field, formatted, "MMYY");
//                             }}
//                           />
//                           <ErrorMessage message={errorMsg} />
//                         </td>
//                       ) : (
//                         <>
//                           <InputCell
//                             key={col.field}
//                             name={col.field}
//                             value={String(row[col.field] ?? "")}
//                             handleChange={(e) =>
//                               handleEmploymentChange(i, col.field, getValue(e))
//                             }
//                             errorMessage={errorMsg}
//                           />
//                         </>
//                       )}
//                     </React.Fragment>
//                   );
//                 })}
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* ── Open Questions */}
//       <div className="mb-3">
//         {employment_textAreaQuestions?.map((ques) => {
//           // console.log(ques.name, errors, errors?.careerGoal);
//           return (
//             <TextareaInput
//               label={ques?.label}
//               name={ques?.name}
//               rows={ques?.rows}
//               value={String(
//                 formData[ques.name as keyof InterviewAssessmentTypes] ?? "",
//               )}
//               onChange={handleChange}
//               // errors={errors}
//               errorMessage={errors?.[ques.name]}
//             />
//           );
//         })}
//       </div>
//       <div className="mb-3">
//         <div className=" pb-2">
//           2.{" "}
//           <span className="fw-semibold form-dark-text">
//             List out any one strength and one weakness?
//           </span>
//         </div>
//         <div className="ps-2">
//           <TextareaInput
//             label={"Write about your Strength"}
//             name={"strength"}
//             rows={2}
//             value={String(formData["strength"] ?? "")}
//             onChange={handleChange}
//             errorMessage={errors?.["strength"]}
//           />
//           <TextareaInput
//             label={"Write about your Weakness"}
//             name={"weakness"}
//             rows={2}
//             value={String(formData["weakness"] ?? "")}
//             onChange={handleChange}
//             errorMessage={errors?.["weakness"]}
//           />
//         </div>
//       </div>

//       {/* ── Employee Relationship */}
//       <div className="mb-3">
//         <p className="mb-2" style={{ fontSize: "0.85rem", color: "#111827" }}>
//           3. Do you have any relationship with any employee currently employed
//           with the Team1 Consulting Pvt Ltd? Please mention details below.
//         </p>
//         <div className="table-responsive">
//           <table className="form-table">
//             <thead>
//               <tr>
//                 <th>Name</th>
//                 <th>Designation</th>
//                 <th>Relationship</th>
//               </tr>
//             </thead>
//             <tbody>
//               {rowsRelation?.map((row, i) => (
//                 <tr key={i}>
//                   {COLUMNS_Relation?.map((col) => {
//                     const errorMsg =
//                       errors?.employee_relations?.[i]?.[
//                         col.field as keyof EmploymentHistory
//                       ];
//                     return (
//                       <>
//                         <InputCell
//                           key={col.field}
//                           name={col.field}
//                           value={String(row[col.field] ?? "")}
//                           handleChange={(e) =>
//                             handleRelationChange(i, col.field, getValue(e))
//                           }
//                           errorMessage={errorMsg}
//                         />
//                       </>
//                     );
//                   })}
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* ── Previous Application */}
//       <div className="mb-3">
//         <p className="mb-2" style={{ fontSize: "0.85rem", color: "#111827" }}>
//           4. Have you applied for any position in Team1 Consulting Pvt Ltd
//           previously? If Yes:
//         </p>
//         <div className="table-responsive">
//           <table className="form-table">
//             <thead>
//               <tr>
//                 <th>Year/Month Applied</th>
//                 <th>Position</th>
//                 <th>Location</th>
//               </tr>
//             </thead>
//             <tbody>
//               {rowsPrevApp?.map((row, i) => (
//                 <tr key={i}>
//                   {COLUMNS_PrevApp?.map((col) => {
//                     const errorMsg =
//                       errors?.prev_applications?.[i]?.[
//                         col.field as keyof EmploymentHistory
//                       ];
//                     return (
//                       <InputCell
//                         key={col.field}
//                         name={col.field}
//                         value={String(row[col.field] ?? "")}
//                         handleChange={(e) =>
//                           handlePrevAppChange(i, col.field, getValue(e))
//                         }
//                         errorMessage={errorMsg}
//                       />
//                     );
//                   })}
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* ── CTC + Conviction */}
//       <div className="table-responsive mb-4">
//         <table className="form-table">
//           <tbody>
//             <tr>
//               <td className="td-label">
//                 Current CTC: <span className="text-danger">*</span>
//               </td>
//               <InputCell
//                 name={"current_ctc"}
//                 type="number"
//                 value={String(formData["current_ctc"] ?? "")}
//                 handleChange={
//                   (value) => handleFieldChange("current_ctc", value)
//                   // setFormData((prev: any) => ({ ...prev, current_ctc: value }))
//                 }
//                 errorMessage={errors?.["current_ctc"]}
//               />
//               <td className="td-label">
//                 Expected CTC: <span className="text-danger">*</span>
//               </td>
//               <InputCell
//                 name={"expected_ctc"}
//                 type="number"
//                 value={String(formData["expected_ctc"] ?? "")}
//                 handleChange={
//                   (value) => handleFieldChange("expected_ctc", value)
//                   // setFormData((prev: any) => ({ ...prev, expected_ctc: value }))
//                 }
//                 errorMessage={errors?.["expected_ctc"]}
//               />
//             </tr>
//             <tr>
//               <td className="td-label" colSpan={2}>
//                 Notice Period required to be given to the present employer if
//                 selected (In Days) <span className="text-danger">*</span>
//               </td>
//               <InputCell
//                 colSpan={3}
//                 type="number"
//                 name="notice_period_days"
//                 value={String(formData["notice_period_days"] ?? "")}
//                 handleChange={(value) =>
//                   handleFieldChange("notice_period_days", value)
//                 }
//                 errorMessage={errors?.["notice_period_days"]}
//               />
//             </tr>
//             <tr>
//               <td className="td-label" colSpan={2}>
//                 Have you ever been convicted in a court of law &nbsp;
//                 <span className="text-danger">*</span>
//               </td>
//               <td colSpan={2}>
//                 <div className="d-flex gap-4">
//                   {["Yes", "No"].map((opt) => (
//                     <RadioCheckboxInput
//                       key={opt}
//                       id={opt}
//                       name="convicted_in_court"
//                       value={opt}
//                       label={opt}
//                       checked={formData?.convicted_in_court === opt}
//                       onChange={(e) =>
//                         handleFieldChange("convicted_in_court", e.target.value)
//                       }
//                     />
//                   ))}
//                 </div>
//                 <ErrorMessage message={errors?.["convicted_in_court"]} />
//               </td>
//             </tr>
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };
